'use client'

import { useState, type ReactNode } from 'react'

import { cx } from '@/lib/utils'

interface MobileAccordionProps {
  title: string
  children: ReactNode
  /** Hide the header entirely on md+ (used for "Our Top Picks" which is only a toggle on mobile) */
  hideTitleOnDesktop?: boolean
  /** Keep the section collapsible and initially closed on desktop too. */
  collapseOnDesktop?: boolean
}

const ChevronIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
)

export const MobileAccordion = ({
  title,
  children,
  hideTitleOnDesktop,
  collapseOnDesktop = false,
}: MobileAccordionProps) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        data-tracking="ignore"
        data-ht-disclosure-trigger={`mobile:${title}`}
        aria-expanded={open}
        className={cx(
          'flex w-full items-center justify-between py-5 text-left',
          collapseOnDesktop
            ? 'cursor-pointer'
            : hideTitleOnDesktop
              ? 'md:hidden'
              : 'md:pointer-events-none md:mb-6 md:py-0'
        )}
        onClick={() => setOpen((expanded) => !expanded)}
      >
        <div className="text-sm font-bold tracking-widest text-slate-900 uppercase">{title}</div>
        <ChevronIcon
          className={cx(
            'h-5 w-5 text-slate-400 transition-transform',
            open && 'rotate-180',
            !collapseOnDesktop && !hideTitleOnDesktop && 'md:hidden'
          )}
        />
      </button>
      <div
        data-ht-disclosure-content
        hidden={collapseOnDesktop && !open}
        className={cx(
          'overflow-hidden transition-all duration-300',
          open ? 'max-h-[2000px]' : 'max-h-0',
          !collapseOnDesktop && 'md:max-h-none'
        )}
      >
        {children}
      </div>
    </>
  )
}
